import {onNextFrame} from '../../domutils.ts';
import {parseViewBoxString, type ViewBoxRect} from '../../features/selectregion_coords.ts';
import type {ViewBoxController} from './viewbox_controller.ts';

const SNIPPET_CLICK_THROUGH_EVENT = 'allspice-snippet-click-thru';

/** Convert saved percentage coordinates into a positive, finite user-space rectangle. */
export function viewCoordinatesToBounds(raw: string | null, base: ViewBoxRect): ViewBoxRect | null {
  const rawParts = raw?.split(',');
  if (!rawParts || rawParts.length !== 4 || rawParts.some((part) => part.trim() === '')) return null;

  const parts = rawParts.map(Number);
  if (parts.some((part) => !Number.isFinite(part))) return null;

  const [left, top, right, bottom] = parts;
  if (right <= left || bottom <= top) return null;
  if (![base.x, base.y, base.w, base.h].every(Number.isFinite) || base.w <= 0 || base.h <= 0) return null;

  const bounds = {
    x: base.x + (left / 100) * base.w,
    y: base.y + (top / 100) * base.h,
    w: ((right - left) / 100) * base.w,
    h: ((bottom - top) / 100) * base.h,
  };
  return Object.values(bounds).every(Number.isFinite) && bounds.w > 0 && bounds.h > 0 ? bounds : null;
}

/**
 * Fit a URL-selected region when explicitly requested with zoom=1.
 * Returns whether a valid fit was applied.
 */
export function fitViewCoordinates(
  queryParameters: URLSearchParams,
  svg: Element | null,
  panZoom: Pick<ViewBoxController, 'fitBounds'> | undefined,
): boolean {
  if (queryParameters.get('zoom') !== '1' || !svg || !panZoom) return false;

  const base = parseViewBoxString(svg.getAttribute('data-base-viewbox'));
  if (!base) return false;
  const bounds = viewCoordinatesToBounds(queryParameters.get('view-coordinates'), base);
  if (!bounds) return false;

  panZoom.fitBounds(bounds);
  return true;
}

/**
 * The snippet component resets pan/zoom synchronously in its click-through
 * listener. Delay this component's fit until every listener has run.
 */
export function afterSnippetClickThrough(event: Event | undefined, callback: () => void): void {
  if (event?.type === SNIPPET_CLICK_THROUGH_EVENT) {
    onNextFrame(callback);
  } else {
    callback();
  }
}
